import { useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { useAuthStore } from '@/store/auth';
import { useBootstrapAuth } from '@/hooks/useBootstrapAuth';
import { BootSplash } from '@/components/BootSplash';
import { appendAuthEvent, logoutWithReason } from '@/lib/auth-events';
import { stashChargerDeepLink } from '@/lib/pendingDeepLink';
import { stickerCharger, stickerStation } from '@/lib/stickerUrl';
// useDailyCheckin removed in Phase 1 of the gamification rules
// update — the daily-checkin endpoint that fired on app-mount is
// gone. Streak counter now ticks on charging-session days.
import { useSkinSync } from '@/hooks/useSkinSync';
import { useEvieVoiceSync } from '@/hooks/useEvieVoiceSync';
import { useLocaleSync } from '@/hooks/useLocaleSync';
import { AppShell, isFullscreenRoute } from '@/components/AppShell';

import { LoginPage } from '@/pages/auth/LoginPage';
import { HostCampaignSignIn } from '@/pages/host-campaign/HostCampaignSignIn';
import { isHostProgramPath } from '@evi/shared';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { JoinPage } from '@/pages/auth/JoinPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { MagicLinkPage } from '@/pages/auth/MagicLinkPage';
import { MagicLinkVerifyPage } from '@/pages/auth/MagicLinkVerifyPage';

import { MapPage } from '@/pages/map/MapPage';
import { AssistantPage } from '@/pages/AssistantPage';
import { ScanPage } from '@/pages/scan/ScanPage';
import { ChargingPage } from '@/pages/charging/ChargingPage';
import { HomeRoute } from '@/pages/HomeRoute';
import { ShortcutsPage } from '@/pages/shortcuts/ShortcutsPage';
import { StationDetailPage } from '@/pages/station/StationDetailPage';
import { PreSessionPage } from '@/pages/pre-session/PreSessionPage';
import { HistoryPage } from '@/pages/history/HistoryPage';
import { FavoritesPage } from '@/pages/favorites/FavoritesPage';
import { CouponsPage } from '@/pages/coupons/CouponsPage';
import { WalletPage } from '@/pages/wallet/WalletPage';
import { FriendsPage } from '@/pages/friends/FriendsPage';
import { FriendDetailPage } from '@/pages/friends/FriendDetailPage';
import { NotificationsPage } from '@/pages/notifications/NotificationsPage';
import { StreaksPage } from '@/pages/streaks/StreaksPage';
import { ChallengesPage } from '@/pages/challenges/ChallengesPage';
import { UnlocksPage } from '@/pages/unlocks/UnlocksPage';

import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { SessionDetailPage } from '@/pages/sessions/SessionDetailPage';
import { SessionCompletePage } from '@/pages/sessions/SessionCompletePage';
import { ProfilePage } from '@/pages/profile/ProfilePage';
import { MyChargersPage } from '@/pages/my-chargers/MyChargersPage';
import { MyFobsPage } from '@/pages/rfid/MyFobsPage';
import { ArcadePage } from '@/pages/arcade/ArcadePage';
import { AddChargerPage } from '@/pages/my-chargers/AddChargerPage';
import { MyChargerDetailPage } from '@/pages/my-chargers/MyChargerDetailPage';
import { MyChargerSessionsPage } from '@/pages/my-chargers/MyChargerSessionsPage';
import { MyChargerEarningsPage } from '@/pages/my-chargers/MyChargerEarningsPage';
import { MyChargerContactsPage } from '@/pages/my-chargers/MyChargerContactsPage';
import { MyChargerContactDetailPage } from '@/pages/my-chargers/MyChargerContactDetailPage';
import { EarnWalkPage } from '@/pages/my-chargers/EarnWalkPage';
import { MyAccessPage } from '@/pages/my-access/MyAccessPage';
import { ConnectCompletePage } from '@/pages/my-account/ConnectCompletePage';
import { LeaderboardPage } from '@/pages/leaderboard/LeaderboardPage';
import { FriendsLeaderboardPage } from '@/pages/leaderboard/FriendsLeaderboardPage';
import { RewardsPage } from '@/pages/rewards/RewardsPage';
import { RewardsHistoryPage } from '@/pages/rewards/RewardsHistoryPage';
import { PaymentMethodsPage } from '@/pages/billing/PaymentMethodsPage';
import { SupportPage } from '@/pages/support/SupportPage';
import { HelpPage } from '@/pages/help/HelpPage';
import { HelpSectionPage } from '@/pages/help/HelpSectionPage';
import { HelpChangelogPage } from '@/pages/help/HelpChangelogPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { PlaceholderPage } from '@/pages/placeholder/PlaceholderPage';
import { ThemesPage } from '@/pages/themes/ThemesPage';
import { JoinFleetPage, FLEET_JOIN_CODE_STORAGE_KEY } from '@/pages/fleet/JoinFleetPage';
import { DriverMiniDashboard } from '@/pages/dashboard/DriverMiniDashboard';
import { MyEvPage } from '@/pages/vehicle/MyEvPage';
import { MyHomePage } from '@/pages/home/MyHomePage';
import { HomeSettingsPage } from '@/pages/home/HomeSettingsPage';
import { HomeInvitePage, HOME_INVITE_STORAGE_KEY } from '@/pages/home/HomeInvitePage';
import { EmailPreferencesPage } from '@/pages/settings/EmailPreferencesPage';
import { PAYMENT_RECEIVED_PATH, PaymentReceivedPage } from '@/pages/billing/PaymentReceivedPage';
import { acceptHomeInvite, joinFleetByCode, acceptFriendInvite } from '@/lib/api';
import { takePendingReferral, referralFromSearch } from '@/lib/pendingReferral';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/store/toast';
import i18n from '@/lib/i18n';
import { StoreCatalogPage } from '@/pages/store/StoreCatalogPage';
import { ProductDetailPage } from '@/pages/store/ProductDetailPage';
import { CartPage } from '@/pages/store/CartPage';
import { StoreOrdersPage } from '@/pages/store/StoreOrdersPage';
import { StoreSuccessPage } from '@/pages/store/StoreSuccessPage';
import { StoreWhyPage } from '@/pages/store/StoreWhyPage';
import { Toast } from '@/components/primitives';
import { Palette } from 'lucide-react';

// ObjectId matcher (24 hex chars) — also accepts 'demo' for the session flow demo
const ID_PATTERN = /^([a-f0-9]{24}|demo)$/;

export function App() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Boot-time silent refresh: restore a session from the durable 30-day
  // refresh cookie when localStorage was evicted but the cookie is still
  // valid. True only on !isAuthenticated loads while the refresh resolves;
  // a live localStorage session returns false immediately (no splash).
  const isBootstrapping = useBootstrapAuth();
  const [path, setPath] = useState(window.location.pathname);
  // Theme DOM sync is handled inside <ThemeProvider> (from @evi/skins).

  // Daily-streak check-in hook removed in Phase 1 of the
  // gamification rules update. Streak counter now ticks on
  // charging-session days (see session-points.ts streak math)
  // rather than on app-open.

  // Keeps the driver's purchased skin live-applied as CSS-var
  // overrides over whatever theme family is base-selected.
  useSkinSync();

  // Hydrates the driver's server-side EVII voice choice into the local
  // voice store so the choice follows them across devices.
  useEvieVoiceSync();
  useLocaleSync();

  // When transitioning from a menu route (Profile, Rewards, etc.) back
  // to a fullscreen route (Map / Scan), we snapshot the current page
  // element and hand it to AppShell so it can slide down off the
  // bottom of the viewport while the fullscreen page mounts underneath.
  const [exitOverlay, setExitOverlay] = useState<ReactNode | null>(null);
  const exitTimerRef = useRef<number | null>(null);
  // `renderPage` closes over `path`; we want navigate() to snapshot the
  // element that matches the CURRENT path (about to be left behind),
  // not the new one. Keeping the latest renderer in a ref avoids having
  // to restructure the whole switch-based router.
  const renderPageRef = useRef<() => ReactNode>(() => null);

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((to: string) => {
    setPath((prev) => {
      // Leaving a menu route into a fullscreen route → capture the
      // element tree that was rendering for `prev` so AppShell can
      // animate it out. 280ms matches the slide-down keyframe length.
      const leavingMenu = !isFullscreenRoute(prev) && isFullscreenRoute(to);
      if (leavingMenu) {
        setExitOverlay(renderPageRef.current());
        if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = window.setTimeout(() => {
          setExitOverlay(null);
          exitTimerRef.current = null;
        }, 280);
      }
      window.history.pushState({}, '', to);
      return to;
    });
    // Forward navigation lands at the top of the new page. The scroll container
    // is <main> (mainFullscreen carries overflow-y:auto for immersive routes,
    // including the in-app store), not the window, so reset both. POP
    // (back/forward) goes through the popstate effect above, which does NOT
    // scroll -- leaving the browser's own restoration in place.
    window.scrollTo(0, 0);
    document.querySelector('main')?.scrollTo({ top: 0, left: 0 });
  }, []);

  // Cross-tab auth-state sync. zustand-persist hydrates from
  // localStorage ONCE on mount and doesn't auto-react to writes
  // from other tabs.
  //
  // Earlier shape blindly rehydrated on every `storage` event for
  // the auth key. That over-fired during token rotation: the
  // /auth/refresh response writes a new access_token into the same
  // persisted blob, and rehydrate() during an in-flight rotation
  // could overwrite the in-memory token a hair before the
  // afterResponse hook stamped it, producing transient 401s.
  //
  // This handler classifies the storage event explicitly:
  //   • old user present, new user absent → genuine logout in
  //     another tab. clearAuth + redirect.
  //   • old user present, new user is a different _id → account
  //     switched in another tab. clearAuth + redirect (this tab
  //     is now stale; safer to land on /login than try to swap
  //     identities mid-render).
  //   • same user, just a new accessToken → token rotation in
  //     another tab. Ignore — afterResponse already handles the
  //     in-tab case, and a cross-tab nudge here would race.
  //   • no old user, new user present → sign-in in another tab.
  //     Ignore. The user will navigate to this tab on their own;
  //     forcing a re-render to authed state without the user
  //     asking for it is jarring.
  //
  // Lives AFTER navigate's useCallback so the dependency array
  // can reference both `clearAuth` and `navigate` without
  // tripping the TDZ on first render.
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key !== 'evi-web-auth') return;
      if (!event.newValue && !event.oldValue) return;

      const parseUser = (raw: string | null) => {
        if (!raw) return null;
        try {
          return JSON.parse(raw)?.state?.user ?? null;
        } catch {
          return null;
        }
      };

      const oldUser = parseUser(event.oldValue);
      const newUser = parseUser(event.newValue);

      // Genuine logout in another tab
      if (oldUser && !newUser) {
        logoutWithReason('storage-event-cross-tab', { trigger: 'logout' });
        navigate('/login');
        return;
      }

      // Account switch in another tab
      if (oldUser && newUser && oldUser._id !== newUser._id) {
        logoutWithReason('storage-event-cross-tab', {
          trigger: 'account-switch',
          oldUserId: oldUser._id,
          newUserId: newUser._id,
        });
        navigate('/login');
        return;
      }

      // Token rotation (same user, new accessToken): ignore.
      // Sign-in in another tab from logged-out state: ignore.
    };

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [navigate]);

  // Boot-time cookie reachability probe. Observational only — does
  // NOT redirect or clear in-memory state. Fires once on mount if
  // the store already thinks we're authenticated; logs a
  // `boot-no-cookie` event when the refresh cookie isn't reaching
  // the server (cross-origin SameSite, proxy misconfiguration,
  // third-party-cookie blocking). The event lands in the ring
  // buffer at localStorage['evi-auth-events'] so the founder can
  // diagnose dev-vs-prod cookie config issues from DevTools.
  //
  // Bare fetch (not the `api` ky instance) because the api hook
  // would interpret a 401 here as a refresh trigger — which is
  // the opposite of what we want for an observational probe.
  useEffect(() => {
    if (!isAuthenticated) return;
    const apiBase = import.meta.env['VITE_API_URL'] ?? '/api';
    fetch(`${apiBase}/auth/cookie-check`, { credentials: 'include' })
      .then((res) => {
        if (res.status !== 200) {
          appendAuthEvent({
            reason: 'boot-no-cookie',
            status: res.status,
            origin: window.location.origin,
            apiBase,
          });
        } else {
          appendAuthEvent({
            reason: 'boot-check',
            status: 200,
            origin: window.location.origin,
          });
        }
      })
      .catch((err: unknown) => {
        appendAuthEvent({
          reason: 'boot-no-cookie',
          error: err instanceof Error ? err.message : String(err),
          origin: window.location.origin,
          apiBase,
        });
      });
    // Run once on mount; subsequent auth state changes don't need
    // re-probing (the user just signed in / out, the refresh hook
    // handles ongoing reachability).
    // The `react-hooks/exhaustive-deps` rule isn't registered in the
    // shared eslint config; intentionally omitting deps here.
  }, []);

  // Home invite link stash (Residential household slice). The
  // /home-invite landing stashes its token when the visitor isn't
  // signed in; this effect accepts it the moment a session exists —
  // register, login, and the Google OAuth bounce all converge here
  // without the landing page needing a revisit. Server-side accept
  // is idempotent, so a double-fire is harmless.
  // One /auth/me reconcile per app open. The persisted PublicUser is
  // whatever the last sign-in or refresh wrote, and sign-in payloads
  // are login-shaped — no shortcut_persona / home_charger_id / pinned
  // shortcuts, no theme changes made on another device. Without this
  // an installed PWA whose localStorage survived never learned any of
  // them (the cookie-restore boot path already refreshes; the
  // localStorage path returned early). refreshUser soft-fails.
  useEffect(() => {
    if (!isAuthenticated) return;
    void useAuthStore.getState().refreshUser();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const token = sessionStorage.getItem(HOME_INVITE_STORAGE_KEY);
    if (!token) return;
    sessionStorage.removeItem(HOME_INVITE_STORAGE_KEY);
    acceptHomeInvite(token)
      .then((result) => {
        if (result.joined || result.already) {
          toast(`You can now charge free at ${result.home_name}`, { tone: 'accent' });
        }
      })
      .catch(() => {
        toast('That home invite link is no longer valid', { tone: 'warn' });
      });
  }, [isAuthenticated]);

  // Friend-link drain (23 October 2026), a twin of the home-invite drain above. main.tsx stashed the link's
  // ?ref= before the first render; once a session exists, however it came (a signup on that page, Sign in with a
  // password, an emailed link, Google, or a session that was already open), this asks the Api to make the sharer
  // and this driver friends. The Api notifies both of them and is idempotent, so a signup or a Google callback
  // that already bound the pair answers already_friends and nobody is pinged twice. The stash is taken BEFORE the
  // call (one shot, it can never attach to a later sign-in on this device) and the ?ref= is dropped from the
  // address bar for the same reason. Every refusal is silent.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!isAuthenticated) return;
    const code = takePendingReferral();
    if (!code) return;
    if (referralFromSearch(window.location.search)) {
      const params = new URLSearchParams(window.location.search);
      params.delete('ref');
      const qs = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
    acceptFriendInvite(code)
      .then((result) => {
        if (result.friend && (result.status === 'created' || result.status === 'accepted_pending' || result.status === 'already_friends')) {
          toast(i18n.t('friends:share.nowFriends', { name: result.friend.name ?? i18n.t('friends:share.yourFriend') }), { tone: 'accent', durationMs: 5000 });
          void queryClient.invalidateQueries({ queryKey: ['friends'] });
        }
      })
      .catch(() => {
        // Not a driver account, offline, or rate limited: the link simply does nothing, as before.
      });
  }, [isAuthenticated, queryClient]);

  // Fleet join-link drain — twin of the home-invite drain above. A
  // /join-fleet?code= landing while signed out parks the code in
  // sessionStorage; once a session exists this posts it exactly as the
  // code-entry form would, then refreshes /auth/me so fleet_id +
  // current_mode land in the shell. The key is cleared BEFORE the POST
  // (JoinFleetPage's own auto-submit does the same) so a driver who signs
  // in and lands back on /join-fleet is never joined twice. i18n.t is
  // used directly because App renders no translation hook; resources are
  // bundled eagerly so the fleet namespace is always present.
  useEffect(() => {
    if (!isAuthenticated) return;
    let code: string | null = null;
    try {
      code = sessionStorage.getItem(FLEET_JOIN_CODE_STORAGE_KEY);
      if (code) sessionStorage.removeItem(FLEET_JOIN_CODE_STORAGE_KEY);
    } catch {
      return;
    }
    if (!code) return;
    const stashedCode = code;
    joinFleetByCode(code)
      .then(async (result) => {
        await useAuthStore.getState().refreshUser();
        toast(
          i18n.t(result.already_member ? 'fleet:code.alreadyThisFleet' : 'fleet:code.welcome', { fleetName: result.fleet_name }),
          { tone: 'accent' },
        );
      })
      .catch(async (err: unknown) => {
        // Two refusals are not the link's fault. Already-in-a-fleet:
        // say so. Cross-tenant (the fleet belongs to a building this
        // account is not in — the common case for a brand-new sign-up,
        // which lands in the default tenant): say what to do AND put
        // the code back, so once the driver joins that building (a
        // re-login, which re-runs this drain) the fleet join completes
        // on its own. Everything else reads as "link no longer works".
        let key = 'fleet:code.linkInvalid';
        if (err instanceof Error && 'response' in err) {
          try {
            const body = await (err as { response: Response }).response.json();
            if (body?.error?.code === 'DRIVER_ALREADY_IN_FLEET') key = 'fleet:code.alreadyInFleet';
            if (body?.error?.code === 'CROSS_TENANT_INVITE') {
              key = 'fleet:code.crossTenant';
              try { sessionStorage.setItem(FLEET_JOIN_CODE_STORAGE_KEY, stashedCode); } catch { /* storage blocked */ }
            }
          } catch {
            // fall through to the generic message
          }
        }
        toast(i18n.t(key), { tone: 'warn', durationMs: 7000 });
      });
  }, [isAuthenticated]);

  // /join — universal public route. Works for both authenticated
  // and unauthenticated users; the page itself decides which fork
  // to show. Routed BEFORE the auth gate below so logged-in users
  // get the inline switch flow rather than a forced login.
  // <Toast /> rides every public branch below as well as the
  // authenticated tree's return: the toast store is global, but the
  // renderer used to mount only inside the authenticated branch, so
  // toasts fired on auth screens (login errors, the Google-callback
  // bounce) silently vanished — and a stale toast could flash after
  // login when the renderer finally mounted. Exactly one branch
  // renders at a time, so the singleton stays a singleton.
  // Hold the first paint on a splash while the boot-time silent refresh
  // resolves, so a driver whose 30-day cookie is valid but whose
  // localStorage was evicted is restored without a Login flash. Only
  // !isAuthenticated loads reach here with isBootstrapping true; a live
  // localStorage session skips the splash entirely (useBootstrapAuth).
  // Placed after every hook above so the early return is Rules-of-Hooks safe.
  if (isBootstrapping) return <BootSplash />;

  const pathOnlyForPublic = path.split('?')[0] ?? path;
  if (isHostProgramPath(pathOnlyForPublic)) {
    return <HostCampaignSignIn signedIn={isAuthenticated} onNavigate={navigate} />;
  }
  if (pathOnlyForPublic === '/join') {
    return (<><JoinPage onNavigate={navigate} /><Toast /></>);
  }

  // Phase 3b Item 2 — fleet invite landing. Public so the email-
  // link branch works for new visitors who don't have an account
  // yet. The page itself fires accept-invite and redirects to
  // /register?pendingToken=… on needs_registration; an existing
  // account gets pending_verification and an offer of a sign-in link.
  if (pathOnlyForPublic === '/join-fleet') {
    return (<><JoinFleetPage onNavigate={navigate} /><Toast /></>);
  }

  // Emailed sign-in link, and the Google hand-off that exits through the
  // same verify. Routed before the auth gate: a pending fleet join applies
  // only at a verified sign-in, so a driver who is already signed in must
  // still reach this page. A verify while signed in replaces the current
  // session with the verified one.
  if (pathOnlyForPublic.startsWith('/auth/magic-verify') || pathOnlyForPublic === '/auth/magic-callback') {
    return (<><MagicLinkVerifyPage onNavigate={navigate} /><Toast /></>);
  }

  // /home-invite — the shareable home invite link's landing (Residential
  // household slice). Public like /join-fleet: signed-in visitors accept
  // inline; signed-out visitors stash the token and go through auth.
  if (pathOnlyForPublic === '/home-invite') {
    return (<><HomeInvitePage onNavigate={navigate} /><Toast /></>);
  }

  // /email-preferences: where every marketing email's Unsubscribe and Email
  // preferences links land (23 October 2026). Public for signed-in and
  // signed-out visitors alike: the signed token in the link is the
  // credential, so it never asks anyone to sign in to unsubscribe.
  if (pathOnlyForPublic === '/email-preferences') {
    return (<><EmailPreferencesPage onNavigate={navigate} /><Toast /></>);
  }

  // /payment-received: where a Stripe Payment Link returns after the payment
  // went through (the v1 pay links, 23 October 2026). Public for signed-in and
  // signed-out visitors alike: paying on Stripe needs no sign-in, so the thank
  // you must not either.
  if (pathOnlyForPublic === PAYMENT_RECEIVED_PATH) {
    return (<><PaymentReceivedPage onNavigate={navigate} /><Toast /></>);
  }

  // Public auth routes (no shell)
  if (!isAuthenticated) {
    // A charger deep link (shared link / field QR) opened while signed
    // out falls through to LoginPage below with the URL intact but no
    // route to it — stash it so the auth pages can replay it instead
    // of landing on /map. No-op for every non-charger path.
    stashChargerDeepLink();
    const publicPage = (() => {
      // /auth/register is the friend link every share made before 23 October 2026 carries
      // (`/auth/register?ref=CODE`). It used to fall through to the Login page, where the code was lost.
      if (pathOnlyForPublic === '/register' || pathOnlyForPublic === '/auth/register') return <RegisterPage onNavigate={navigate} />;
      if (pathOnlyForPublic === '/auth/forgot') return <ForgotPasswordPage onNavigate={navigate} />;
      if (pathOnlyForPublic.startsWith('/auth/reset')) return <ResetPasswordPage onNavigate={navigate} />;
      if (pathOnlyForPublic === '/auth/magic') return <MagicLinkPage onNavigate={navigate} />;
      return <LoginPage onNavigate={navigate} />;
    })();
    return (<>{publicPage}<Toast /></>);
  }

  const renderPage = () => {
    // The `path` state may carry a query string when navigation was
    // internal (navigate('/charging-station/X?charger=Y') via
    // pushState); on a direct landing it's just the pathname. Strip
    // the query for regex matching; read query params from
    // window.location.search separately when needed.
    const pathOnly = path.split('?')[0] ?? path;

    // ── Parametric routes ──────────────────────────────────────────────
    // /charging-station/:locationId?charger=:chargerIdentity — the
    // canonical v1 QR sticker format. Production stickers in the
    // field today scan to this URL; v2 must keep them working
    // forever. Routed FIRST so the URL bar post-scan reflects the
    // sticker format the driver scanned.
    //
    // chargerIdentity comes from the `charger` query param and
    // accepts the loose OCPP identity alphabet (alphanumeric +
    // underscore + hyphen) since QR tags normally carry the identity
    // string the charger self-reports on BootNotification.
    // The sticker parts come from lib/stickerUrl, the same helper the
    // in-app scanner uses: trailing slash, upper-case hex and the charger
    // query-name aliases are accepted alike (P0 2026-10-23).
    const stickerStationId = stickerStation(pathOnly);
    if (stickerStationId) {
      const chargerIdentity = stickerCharger(new URLSearchParams(window.location.search));
      if (chargerIdentity) {
        return (
          <PreSessionPage
            locationId={stickerStationId}
            chargerIdentity={chargerIdentity}
            onNavigate={navigate}
          />
        );
      }
      // No / invalid charger param: the bare-station page, so
      // /charging-station/<loc> with a missing query still lands the
      // driver on the StationDetailPage rather than a 404.
      return <StationDetailPage locationId={stickerStationId} onNavigate={navigate} />;
    }

    // /stations/:locationId/stall/:chargerIdentity — v2 internal-
    // navigation format. Kept parseable for any deep links already
    // generated in v2's lifetime (StationDetailPage's stall picker,
    // shared session links). Routed BEFORE the bare
    // /stations/:locationId match (which is a prefix of this one) so
    // the longer form wins.
    //
    // chargerIdentity accepts the loose OCPP identity alphabet
    // (alphanumeric + underscore + hyphen) rather than strict
    // ObjectId hex, since QR tags normally carry the identity string
    // the charger self-reports on BootNotification.
    const preSessionMatch = pathOnly.match(/^\/stations\/([A-Za-z0-9][A-Za-z0-9-]*)\/stall\/([A-Za-z0-9][A-Za-z0-9_-]*)\/?$/);
    if (preSessionMatch) {
      return (
        <PreSessionPage
          locationId={preSessionMatch[1]!}
          chargerIdentity={preSessionMatch[2]!}
          onNavigate={navigate}
        />
      );
    }

    const stationMatch = pathOnly.match(/^\/stations\/([A-Za-z0-9][A-Za-z0-9-]*)\/?$/);
    if (stationMatch) {
      return <StationDetailPage locationId={stationMatch[1]!} onNavigate={navigate} />;
    }

    const chargingMatch = path.match(/^\/charging\/(.+)$/);
    if (chargingMatch) {
      return <ChargingPage sessionId={chargingMatch[1]!} onNavigate={navigate} />;
    }

    const completeMatch = path.match(/^\/sessions\/(.+)\/complete$/);
    if (completeMatch) {
      return <SessionCompletePage sessionId={completeMatch[1]!} onNavigate={navigate} />;
    }

    const receiptMatch = path.match(/^\/sessions\/(.+)\/receipt$/);
    if (receiptMatch && ID_PATTERN.test(receiptMatch[1]!)) {
      return <SessionDetailPage key={receiptMatch[1]} sessionId={receiptMatch[1]!} onNavigate={navigate} receiptMode />;
    }
    const sessionMatch = path.match(/^\/sessions\/(.+)$/);
    if (sessionMatch && ID_PATTERN.test(sessionMatch[1]!)) {
      return <SessionDetailPage sessionId={sessionMatch[1]!} onNavigate={navigate} />;
    }

    // /friends/:userId — per-friend detail page. Backend gates
    // the lookup on accepted friendship; the page renders a
    // 404 view when no friendship exists, so direct URL paste
    // for non-friends doesn't leak data.
    const friendDetailMatch = path.match(/^\/friends\/([a-f0-9]{24})$/);
    if (friendDetailMatch) {
      return <FriendDetailPage userId={friendDetailMatch[1]!} onNavigate={navigate} />;
    }

    // /my-chargers/:id (and sub-tabs). Parametric routes must run
    // BEFORE the static /my-chargers/add case so the regex doesn't
    // accidentally swallow it — but the regex is gated on the
    // 24-hex ObjectId pattern, which 'add' fails, so this is
    // belt-and-braces. Sub-tabs (sessions/earnings/contacts) render
    // placeholders for now; commit 3.2 ships sessions + earnings
    // and Phase 4 ships contacts.
    // /my-chargers/:id/contacts/:userId — one contact's charging
    // history at this charger (owner-only). Matched BEFORE the
    // sub-tab regex, which is anchored and wouldn't match the
    // deeper path anyway; ordering keeps the intent obvious.
    const contactDetailMatch = pathOnly.match(
      /^\/my-chargers\/([a-f0-9]{24})\/contacts\/([a-f0-9]{24})$/,
    );
    if (contactDetailMatch) {
      return (
        <MyChargerContactDetailPage
          chargerId={contactDetailMatch[1]!}
          contactUserId={contactDetailMatch[2]!}
          onNavigate={navigate}
        />
      );
    }
    const myChargerSubMatch = pathOnly.match(/^\/my-chargers\/([a-f0-9]{24})\/(sessions|earnings|contacts|earn)$/);
    if (myChargerSubMatch) {
      const cid = myChargerSubMatch[1]!;
      const sub = myChargerSubMatch[2]!;
      // /my-chargers/:id/earn — the earn walk (frictionless flow, piece
      // 3): price → hours → get paid, then enrol. Entered from the
      // post-registration question, the charger page's earn card and
      // Home Settings' Monetize row.
      if (sub === 'earn') {
        return <EarnWalkPage chargerId={cid} onNavigate={navigate} />;
      }
      if (sub === 'sessions') {
        return <MyChargerSessionsPage chargerId={cid} onNavigate={navigate} />;
      }
      if (sub === 'earnings') {
        return <MyChargerEarningsPage chargerId={cid} onNavigate={navigate} />;
      }
      // contacts (Phase 4.1) — pass-list contact cards with
      // tap-to-text/call/email and editable per-contact notes.
      return <MyChargerContactsPage chargerId={cid} onNavigate={navigate} />;
    }
    const myChargerDetailMatch = pathOnly.match(/^\/my-chargers\/([a-f0-9]{24})$/);
    if (myChargerDetailMatch) {
      return (
        <MyChargerDetailPage
          chargerId={myChargerDetailMatch[1]!}
          onNavigate={navigate}
        />
      );
    }

    // /help/:slug — the routed manual. /help (the table of contents) stays a
    // static route below. /help/whats-new renders the changelog; every other
    // slug renders its manual section (HelpSectionPage handles unknown slugs).
    // Slugs are EVII's deep-link contract (manualRouteForSlug in @evi/content).
    const helpMatch = pathOnly.match(/^\/help\/([a-z0-9-]+)$/);
    if (helpMatch) {
      const slug = helpMatch[1]!;
      if (slug === 'whats-new') return <HelpChangelogPage onNavigate={navigate} />;
      return <HelpSectionPage slug={slug} onNavigate={navigate} />;
    }

    // /store/product/:id — one catalog product. 24-hex ObjectId only, mirroring
    // the station-detail parametric pattern; the captured id passes as a prop.
    const storeProductMatch = pathOnly.match(/^\/store\/product\/([a-f0-9]{24})$/);
    if (storeProductMatch) {
      return <ProductDetailPage id={storeProductMatch[1]!} onNavigate={navigate} />;
    }

    // /store/why/:icp — the three ICP pages behind the catalog's
    // customer-voices row. Closed enum (the myChargerSubMatch style)
    // since the set is fixed; unknown slugs fall through to 404.
    const storeWhyMatch = pathOnly.match(/^\/store\/why\/(homeowner|property-manager|electrician)$/);
    if (storeWhyMatch) {
      return <StoreWhyPage icp={storeWhyMatch[1]!} onNavigate={navigate} />;
    }

    // ── Static routes ──────────────────────────────────────────────────
    // Switch on `pathOnly` (no query string) so URLs like
    // `/friends?tab=requests` (used by the friend_request_received
    // notification deep-link) match the static `/friends` case
    // instead of falling through to the catch-all. Per-page
    // query-param parsing happens via window.location.search at
    // page mount, which is independent of the route match.
    switch (pathOnly) {
      // Bare-URL cold-start lands on the HOME: the map — Find Charger
      // is the app's job-to-be-done — unless the driver has a session
      // in flight, in which case the active session owns the screen
      // (HomeRoute; frictionless flow piece 2). '/map' is always the
      // map so the Find Charger tab keeps finding chargers mid-session.
      // Bare '/charging' (the old push-tap target) is the home too.
      case '/':
      case '/charging':
        return <HomeRoute onNavigate={navigate} />;
      case '/map':
      case '/chargers':
        return <MapPage onNavigate={navigate} />;

      case '/scan':
        return <ScanPage onNavigate={navigate} />;

      // Dashboard / stats (accessible via Profile menu)
      case '/home':
      case '/dashboard':
        return <DashboardPage onNavigate={navigate} />;

      case '/sessions':
      case '/history':
        return <HistoryPage onNavigate={navigate} />;
      case '/profile':
        return <ProfilePage onNavigate={navigate} />;
      // Phase 3b Item 3 — fleet driver mini-dashboard.
      case '/me/fleet-stats':
        return <DriverMiniDashboard onNavigate={navigate} />;
      case '/rewards':
        return <RewardsPage onNavigate={navigate} />;
      case '/rewards/history':
        return <RewardsHistoryPage onNavigate={navigate} />;
      case '/leaderboard':
        return <LeaderboardPage onNavigate={navigate} />;
      case '/leaderboard/friends':
        return <FriendsLeaderboardPage onNavigate={navigate} />;
      case '/billing':
      case '/billing/payment-methods':
      // The card-declined email links here, singular (Ocpp StopTransaction
      // retry_url); emails already sent keep that link, so it stays an alias.
      case '/billing/payment-method':
        return <PaymentMethodsPage onNavigate={navigate} />;
      case '/wallet':
        return <WalletPage onNavigate={navigate} />;
      case '/friends':
        return <FriendsPage onNavigate={navigate} />;
      case '/notifications':
        return <NotificationsPage onNavigate={navigate} />;
      case '/streaks':
        return <StreaksPage onNavigate={navigate} />;
      case '/challenges':
        return <ChallengesPage onNavigate={navigate} />;
      case '/unlocks':
        return <UnlocksPage onNavigate={navigate} />;
      case '/settings':
        return <SettingsPage onNavigate={navigate} />;
      case '/favorites':
        return <FavoritesPage onNavigate={navigate} />;
      // /my-chargers — resident-managed list (Phase 2 of the
      // peer-to-peer ticket). /my-chargers/add hosts the self-service
      // registration flow; the list is the empty-state-aware landing.
      case '/my-chargers':
        return <MyChargersPage onNavigate={navigate} />;
      // /my-fobs — the driver's RFID cards + tap-capture self-
      // registration flow (Cards & fobs in the Money sheet).
      case '/my-fobs':
        return <MyFobsPage onNavigate={navigate} />;
      // /arcade — hidden demo surface. Not in any nav; the page itself
      // gates on the driver's unlock flag and bounces to the map.
      case '/arcade':
        return <ArcadePage onNavigate={navigate} />;
      case '/my-chargers/add':
        return <AddChargerPage onNavigate={navigate} />;
      // /my-access — bidirectional twin of My Chargers > Contacts.
      // Shows the caller every charger they've been pass-list-added
      // to (cross-tenant). Empty state is the common path; most
      // drivers won't be on anyone's list.
      case '/my-access':
        return <MyAccessPage onNavigate={navigate} />;
      // /my-account/connect/complete — Stripe-hosted onboarding
      // returns the resident here (refresh and return URLs both
      // point to this path). The page reads /me/connect/status and
      // shows the next step. Phase 6.1.
      case '/my-account/connect/complete':
        return <ConnectCompletePage onNavigate={navigate} />;
      case '/vehicle':
        return <MyEvPage onNavigate={navigate} />;
      case '/my-home':
        return <MyHomePage onNavigate={navigate} />;
      case '/my-home/settings':
        return <HomeSettingsPage onNavigate={navigate} />;
      case '/skins':
        return (
          <PlaceholderPage
            onNavigate={navigate}
            icon={Palette}
            eyebrow="Personalize your ride"
            title="Skins & themes"
            description="Unlock visual skins by earning badges and reaching new ranks. Equip them from this screen soon."
          />
        );
      case '/coupons':
        return <CouponsPage onNavigate={navigate} />;
      case '/store':
        return <StoreCatalogPage onNavigate={navigate} />;
      case '/store/cart':
        return <CartPage onNavigate={navigate} />;
      // Owner-scoped order history (GET /store/orders/mine) — reached
      // from the Profile "My Orders" row and the checkout success page.
      case '/store/orders':
        return <StoreOrdersPage onNavigate={navigate} />;
      // Stripe Checkout return URL (?session_id=cs_...). The page reads the
      // query param itself; pathOnly matching means the param never affects
      // the route match.
      case '/store/success':
        return <StoreSuccessPage onNavigate={navigate} />;
      // Phase 1 of the support-ticket system. DriverAssistSheet's
      // "Contact support" CTA deep-links here with URL params
      // (?charger=&location= from PreSessionPage, ?session= from
      // ChargingPage); the page reads them as optional context and
      // submits to POST /me/support. Direct visits work too —
      // ticket gets source='manual' with no context block.
      case '/support':
        return <SupportPage onNavigate={navigate} />;
      case '/assistant':
        return <AssistantPage />;
      case '/help':
        return <HelpPage onNavigate={navigate} />;
      case '/themes':
        return <ThemesPage onNavigate={navigate} />;
      // The three home-screen pins picker (frictionless flow, piece 1).
      case '/shortcuts':
        return <ShortcutsPage onNavigate={navigate} />;
      default:
        return <MapPage onNavigate={navigate} />;
    }
  };

  // Keep the ref pointing at the current renderer so `navigate()` can
  // snapshot the outgoing element tree against the CURRENT path before
  // React starts re-rendering for the new path.
  renderPageRef.current = renderPage;

  return (
    <>
      <AppShell currentPath={path} onNavigate={navigate} exitOverlay={exitOverlay}>
        {renderPage()}
      </AppShell>
      {/* Singleton toast renderer — reads from useToastStore and fires
          from any caller (picker, session, wallet…). Mounted once PER
          BRANCH (the public auth returns above mount their own copy)
          so auth screens get toasts too; only one branch renders at a
          time, so it stays a singleton. */}
      <Toast />
    </>
  );
}
